import { useCallback, useEffect, useState } from 'react'
import { useState as useLocalState } from 'react'
import {
  Loader2, Smartphone, ShieldCheck, ShieldOff, RotateCcw, Ban, Eye, TimerReset, Trash2,
} from 'lucide-react'
import { Note } from '@/shared/ui/states'
import { explain } from '@/shared/api/problem'
import { useCan } from '@/shared/auth/session'
import { fmtDateTime } from '@/shared/lib/format'
import {
  listUserDevices, activateDevice, revokeDevice, suspendDevice, restoreDevice,
  resetDeviceActivation, revealDeviceFingerprint, forgetDevice,
  type DeviceBinding, type DeviceFingerprint,
} from './api'
import { confirmDanger } from '@/shared/ui/confirm'

/**
 * The devices bound to one account.
 *
 * ## This is the screen somebody opens when a phone is stolen
 *
 * So it has to answer, in one look: which handsets can reach this account, when
 * each was last used, and who let it in. Everything else is secondary to that.
 *
 * ## Activation is the second half of a two-person act
 *
 * The person holding the phone reads out a six-digit code; whoever holds
 * `device:activate` types it here. The code travels over a channel an attacker
 * is not on, which is what proves the two people are talking to each other —
 * without it, approving "the pending device for Jane" is approving a row that
 * anyone who knows Jane's password could have created.
 *
 * The fingerprint below each pending device — model, OS, the platform's own
 * identifier — is shown for exactly one purpose: so the activator can ask "is
 * that the phone you're holding?" It is client-asserted and is never checked by
 * the server, so it is evidence for a human judgement and nothing more.
 */
export function DevicesPanel({ userId, userName }: { userId: string; userName: string }) {
  const canRead = useCan('device:read')
  const canActivate = useCan('device:activate')
  const canRevoke = useCan('device:revoke')
  const canAdmin = useCan('device:admin')

  const [devices, setDevices] = useState<DeviceBinding[] | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState<string | null>(null)
  const [note, setNote] = useState<{ tone: 'good' | 'bad'; text: string } | null>(null)

  const load = useCallback(async () => {
    if (!canRead) return
    try {
      setDevices(await listUserDevices(userId))
      setError(null)
    } catch (err) {
      setError(explain(err))
    }
  }, [userId, canRead])

  useEffect(() => { void load() }, [load])

  if (!canRead) return null

  async function run(id: string, fn: () => Promise<unknown>, ok: string) {
    setBusy(id)
    setNote(null)
    try {
      await fn()
      await load()
      setNote({ tone: 'good', text: ok })
    } catch (err) {
      setNote({ tone: 'bad', text: explain(err) })
    } finally {
      setBusy(null)
    }
  }

  if (devices === null && !error) {
    return (
      <div className="px-4 py-6 text-erp-sm text-muted-foreground">
        <Loader2 className="h-4 w-4 animate-spin inline mr-2" /> Loading devices…
      </div>
    )
  }

  return (
    <div className="space-y-3">
      {error && <Note tone="bad">{error}</Note>}
      {note && <Note tone={note.tone} onDismiss={() => setNote(null)}>{note.text}</Note>}

      {devices?.length === 0 && (
        <Note tone="info">
          No devices. {userName} cannot use the field app until a phone is enrolled
          from the app and activated here.
        </Note>
      )}

      {devices?.map((d) => (
        <DeviceRow
          key={d.id}
          device={d}
          busy={busy === d.id}
          canActivate={canActivate}
          canRevoke={canRevoke}
          canAdmin={canAdmin}
          onActivate={(code) => run(d.id, () => activateDevice(d.id, code), 'Device activated.')}
          onRevoke={() => run(d.id, () => revokeDevice(d.id, 'Revoked from the Users screen'),
            'Device revoked. Its sessions stop within 15 minutes.')}
          onSuspend={() => run(d.id, () => suspendDevice(d.id, 'Suspended from the Users screen'),
            'Device suspended. The binding is kept.')}
          onRestore={() => run(d.id, () => restoreDevice(d.id), 'Device restored.')}
          onReset={() => run(d.id, () => resetDeviceActivation(d.id),
            'Attempts cleared and the window reopened. The code on their phone is unchanged.')}
          onForget={async () => {
            const ok = await confirmDanger({
              title: 'Forget this device?',
              html:
                `This deletes the binding between <b>${userName}</b> and this handset. `
                + 'The phone signs out and returns to the sign-in screen, where it must '
                + 'enrol again and show a fresh six-digit code for someone to activate.'
                + '<br><br>Use this when the binding is broken or the phone is being '
                + 'handed to someone else. To lock a stolen phone out while keeping the '
                + 'record, use <b>Revoke</b> instead.',
              confirmText: 'Forget device',
            })
            if (ok) {
              await run(d.id, () => forgetDevice(d.id),
                'Device forgotten. The phone will ask to enrol again.')
            }
          }}
        />
      ))}
    </div>
  )
}

function Ident({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex gap-2 text-erp-xs">
      <span className="text-muted-foreground w-32 shrink-0 truncate">{label}</span>
      <span className="font-mono break-all">{value}</span>
    </div>
  )
}

const STATUS_STYLE: Record<DeviceBinding['status'], string> = {
  PENDING: 'bg-amber-100 text-amber-900 dark:bg-amber-950/40 dark:text-amber-200',
  ACTIVE: 'bg-emerald-100 text-emerald-900 dark:bg-emerald-950/40 dark:text-emerald-200',
  SUSPENDED: 'bg-slate-200 text-slate-800 dark:bg-slate-800 dark:text-slate-200',
  REVOKED: 'bg-red-100 text-red-900 dark:bg-red-950/40 dark:text-red-200',
}

function DeviceRow({
  device, busy, canActivate, canRevoke, canAdmin,
  onActivate, onRevoke, onSuspend, onRestore, onReset, onForget,
}: {
  device: DeviceBinding
  busy: boolean
  canActivate: boolean
  canRevoke: boolean
  canAdmin: boolean
  onActivate: (code: string) => void
  onRevoke: () => void
  onSuspend: () => void
  onRestore: () => void
  onReset: () => void
  onForget: () => void
}) {
  const [code, setCode] = useState('')
  const [print, setPrint] = useLocalState<DeviceFingerprint | null>(null)
  const [revealing, setRevealing] = useLocalState(false)
  const pending = device.status === 'PENDING'

  async function reveal() {
    setRevealing(true)
    try {
      setPrint(await revealDeviceFingerprint(device.id))
    } finally {
      setRevealing(false)
    }
  }

  return (
    <div className="border border-border rounded-md p-3">
      <div className="flex items-start gap-3">
        <div className="h-9 w-9 rounded-lg bg-muted grid place-items-center shrink-0">
          <Smartphone className="h-4 w-4 text-muted-foreground" />
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <span className="text-erp-sm font-medium truncate">
              {device.label || device.model || device.platform}
            </span>
            <span className={`text-[10px] font-semibold uppercase tracking-wide px-2 py-0.5 rounded ${STATUS_STYLE[device.status]}`}>
              {device.status}
            </span>
          </div>
          {/* The fingerprint. Shown so a human can ask "is that your phone?" —
              never checked by the server. */}
          <div className="text-erp-xs text-muted-foreground mt-0.5">
            {[device.model, device.osVersion, device.appVersion && `app ${device.appVersion}`]
              .filter(Boolean).join(' · ')}
          </div>
          <div className="text-erp-xs text-muted-foreground mt-0.5">
            {device.lastSeenAt ? `Last seen ${fmtDateTime(device.lastSeenAt)}` : 'Never used'}
            {device.activatedAt && ` · Activated ${fmtDateTime(device.activatedAt)}`}
          </div>
          {device.attestation === 'NONE' && (
            <div className="text-erp-xs mt-1 text-amber-700 dark:text-amber-300">
              Not attested — this build could not prove it is genuine. Refused in production.
            </div>
          )}
          {device.revokeReason && (
            <div className="text-erp-xs text-muted-foreground mt-0.5">{device.revokeReason}</div>
          )}

          {/* What actually identifies this handset. Behind a reveal because these are
              persistent per-device handles — the value that says "this is the same
              physical phone" across accounts and across time. Every reveal is audited,
              and the button says so before it is pressed rather than after. */}
          {print === null ? (
            <button
              className="erp-btn-ghost text-erp-xs mt-2 inline-flex items-center gap-1.5 text-chocolate"
              disabled={revealing}
              onClick={reveal}
            >
              {revealing
                ? <Loader2 className="h-3 w-3 animate-spin" />
                : <Eye className="h-3 w-3" />}
              Reveal device identifiers — this is logged
            </button>
          ) : (
            <div className="mt-2 rounded-md border border-border bg-muted/40 p-2 space-y-1">
              <Ident label="Device ID" value={print.deviceId} />
              {print.publicKeyDigest
                ? <Ident label="Attested key" value={print.publicKeyDigest} />
                : <Ident label="Attested key" value="none — this build is not attested" />}
              {Object.entries(print.platformIdentifiers).map(([k, v]) => (
                <Ident key={k} label={k} value={String(v)} />
              ))}
              <p className="text-erp-xs text-muted-foreground pt-1">
                These identify the handset. They authenticate nothing — the binding is the
                attested key — and your name is on the record for opening this.
              </p>
            </div>
          )}
        </div>
      </div>

      {pending && canActivate && (
        <div className="mt-3 pt-3 border-t border-border">
          <label className="text-erp-xs text-muted-foreground block mb-1.5">
            Ask them to read out the 6-digit code shown on the phone
          </label>
          <div className="flex gap-2">
            <input
              value={code}
              onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
              inputMode="numeric"
              placeholder="000000"
              aria-label="Activation code"
              className="erp-input w-32 font-mono tracking-[0.3em] text-center"
            />
            <button
              className="erp-btn erp-btn-primary"
              disabled={busy || code.length !== 6}
              onClick={() => onActivate(code)}
            >
              {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <ShieldCheck className="h-3.5 w-3.5" />}
              Activate
            </button>
          </div>
          <p className="text-erp-xs text-muted-foreground mt-2">
            After the allowed attempts this code stops working. It expires on its own
            shortly, so a lockout never outlasts the code — they reopen the app for a new
            one, or you reset the attempts below.
          </p>
          {canRevoke && (
            <button
              className="erp-btn mt-2"
              disabled={busy}
              onClick={onReset}
              title="Clears the wrong-code count and reopens the window. The code itself does not change."
            >
              <TimerReset className="h-3.5 w-3.5" /> Reset attempts
            </button>
          )}
        </div>
      )}

      {(canRevoke || canActivate || canAdmin) && (
        <div className="mt-3 pt-3 border-t border-border flex flex-wrap gap-2">
          {device.status !== 'REVOKED' && device.status === 'SUSPENDED' && canActivate && (
            <button className="erp-btn" disabled={busy} onClick={onRestore}>
              <RotateCcw className="h-3.5 w-3.5" /> Restore
            </button>
          )}
          {device.status !== 'REVOKED' && device.status === 'ACTIVE' && canRevoke && (
            <button className="erp-btn" disabled={busy} onClick={onSuspend}>
              <ShieldOff className="h-3.5 w-3.5" /> Suspend
            </button>
          )}
          {device.status !== 'REVOKED' && canRevoke && (
            <button className="erp-btn text-red-700 dark:text-red-300" disabled={busy} onClick={onRevoke}>
              <Ban className="h-3.5 w-3.5" /> Revoke
            </button>
          )}
          {/*
            Offered for EVERY status, revoked included — unlike the three above. Revoke is a
            verdict on a device you still know about; forget is how a binding stops existing
            so the handset can enrol again. A revoked phone whose owner has been cleared needs
            exactly this, and so does one holding an id for a row that is already gone.
          */}
          {canAdmin && (
            <button className="erp-btn text-amber-700 dark:text-amber-300" disabled={busy}
                    onClick={onForget}>
              <Trash2 className="h-3.5 w-3.5" /> Forget device
            </button>
          )}
        </div>
      )}
    </div>
  )
}
